import React, { useEffect, useMemo, useState } from "react";
import { 
  collection, getDocs, query, orderBy, addDoc, doc, updateDoc, serverTimestamp, where, limit, getDoc, setDoc
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../../../context/AuthContext";

const byLocale = (a, b) => a.localeCompare(b, "tr", { sensitivity: "base" });

const formatDate = (ts) => {
  if (!ts) return "-";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString("tr-TR");
};

// Sabit Seçenek Listeleri
const CINS_LISTESI = [ "Pompa", "Mikser", "Binek", "Ticari", "Minibüs", "Kamyonet", "Çekici", "Silobas", "Diğer"];
const DESIGN_MODULE = "filo_arac_listesi";

const toTitleCaseTR = (value) => String(value || "")
  .toLocaleLowerCase("tr")
  .replace(/(^|\s)\S/g, (char) => char.toLocaleUpperCase("tr"));

const DEFAULT_COLUMNS = [
  { key: "plate", label: "PLAKA", width: "130px", visible: true, render: (a) => a.plate, bold: true },
  { key: "aracCinsi", label: "CİNS", visible: true, render: (a) => a.aracCinsi },
  { key: "tesis", label: "TESİS", visible: true, render: (a) => a.tesis },
  { key: "model", label: "MODEL TARİHİ", visible: true, render: (a) => a.model },
  { key: "bomUzunlugu", label: "BOM", visible: true, render: (a) => a.bomUzunlugu },
  { key: "ustGrupMarka", label: "ÜST GRUP MARKA", visible: false, render: (a) => a.ustGrupMarka },
  { key: "ustGrupModel", label: "ÜST GRUP MODEL", visible: false, render: (a) => a.ustGrupModel },
  { key: "aracMarka", label: "ARAÇ MARKA", visible: false, render: (a) => a.aracMarka },
  { key: "aracModel", label: "ARAÇ MODELİ", visible: false, render: (a) => a.aracModel },
  { key: "netAgirlik", label: "NET AĞIRLIĞI", visible: false, render: (a) => a.netAgirlik },
  { key: "azamiYukluAgirlik", label: "AZAMİ YÜKLÜ AĞIRLIĞI", visible: false, render: (a) => a.azamiYukluAgirlik },
  { key: "active", label: "DURUM", visible: true, align: "center", render: (a) => a.active ? "AKTİF" : "PASİF" },
  { key: "createdTime", label: "KAYIT TARİHİ", visible: true, render: (a) => formatDate(a.createdTime) },
  { key: "updatedTime", label: "GÜNCELLEME TARİHİ", visible: true, render: (a) => formatDate(a.updatedTime) },
];

const AracListesi = () => {
  const { user, role } = useAuth();
  const isCreator = (role || "").toLocaleLowerCase("tr") === "oluşturucu";

  const [araclar, setAraclar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tesisOpen, setTesisOpen] = useState({});
  const [izinliTesisler, setIzinliTesisler] = useState(null); // null = kısıtlama yok
  const [moreOpen, setMoreOpen] = useState(false);
  const [columnLayout, setColumnLayout] = useState(DEFAULT_COLUMNS.map(c => ({ key: c.key, visible: c.visible })));

  // --- GELİŞMİŞ SÜTUN FİLTRELERİ ---
  const [colFilters, setColFilters] = useState({
    plate: "",
    aracCinsi: "HEPSİ",
    tesis: "HEPSİ",
    model: "HEPSİ",
    bomUzunlugu: "HEPSİ",
    active: "HEPSİ",
    createdStart: "",
    createdEnd: "",
    updatedStart: "",
    updatedEnd: "",
    ustGrupMarka: "HEPSİ",
    ustGrupModel: "HEPSİ",
    aracMarka: "HEPSİ",
    aracModel: "HEPSİ",
    netAgirlik: "HEPSİ",
    azamiYukluAgirlik: "HEPSİ"
  });

  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    plate: "", aracCinsi: "", tesis: "", model: "", 
    bomUzunlugu: "", ruhsatSeriNo: "", motorNo: "", saseNo: "",
    ustGrupMarka: "", ustGrupModel: "", aracMarka: "", aracModel: "",
    netAgirlik: "", azamiYukluAgirlik: "",
    active: true
  });

  const fetchAraclar = async (izinli) => {
    try {
      setLoading(true);
      const q = query(collection(db, "plates"), orderBy("plate", "asc"));
      const querySnapshot = await getDocs(q);
      let allData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Tesis filtresi uygula
      if (izinli !== null && Array.isArray(izinli)) {
        allData = allData.filter(a => izinli.includes(String(a.tesis || "").trim()));
      }
      setAraclar(allData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const init = async () => {
      let izinli = null;
      if (!isCreator && user?.email) {
        try {
          const perSnap = await getDocs(
            query(collection(db, "personeller"), where("email", "==", user.email), limit(1))
          );
          if (!perSnap.empty) {
            const pData = perSnap.docs[0].data();
            const kendi = pData.tesis ? [String(pData.tesis).trim()] : [];
            const ekstra = Array.isArray(pData.gorebilecegiFiloTesisler)
              ? pData.gorebilecegiFiloTesisler.map(t => String(t).trim()) : [];
            izinli = [...new Set([...kendi, ...ekstra])];
          } else {
            izinli = []; // personeller'de kaydı yok → hiçbir araç göremesin
          }
        } catch (e) { console.error("Tesis filtresi hatası:", e); izinli = []; }
      }
      setIzinliTesisler(izinli);
      await fetchAraclar(izinli);
    };
    init();
  }, [user, isCreator]);

  useEffect(() => {
    const loadDesign = async () => {
      if (!user?.email) return;
      try {
        const designId = `${DESIGN_MODULE}_${encodeURIComponent(user.email)}`;
        const snap = await getDoc(doc(db, "reportDesigns", designId));
        const saved = snap.exists() ? snap.data()?.columns : null;
        if (!Array.isArray(saved)) return;
        const validKeys = new Set(DEFAULT_COLUMNS.map(c => c.key));
        const savedClean = saved
          .filter(c => c?.key && validKeys.has(c.key))
          .map(c => ({ key: c.key, visible: c.visible !== false }));
        const missing = DEFAULT_COLUMNS
          .filter(c => !savedClean.some(s => s.key === c.key))
          .map(c => ({ key: c.key, visible: c.visible }));
        setColumnLayout([...savedClean, ...missing]);
      } catch (err) {
        console.error("Araç listesi tasarımı yüklenemedi:", err);
      }
    };
    loadDesign();
  }, [user?.email]);

  const uniqueVals = useMemo(() => {
    const res = { cins: [], tesis: [], model: [], bom: [], ustGrupMarka: [], ustGrupModel: [], aracMarka: [], aracModel: [], netAgirlik: [], azamiYukluAgirlik: [] };
    araclar.forEach(a => {
      const c = a.aracCinsi ? String(a.aracCinsi).trim() : "";
      const t = a.tesis ? String(a.tesis).trim() : "";
      const m = a.model ? String(a.model).trim() : "";
      const b = a.bomUzunlugu ? String(a.bomUzunlugu).trim() : "";
      const ugm = a.ustGrupMarka ? String(a.ustGrupMarka).trim() : "";
      const ugmo = a.ustGrupModel ? String(a.ustGrupModel).trim() : "";
      const am = a.aracMarka ? String(a.aracMarka).trim() : "";
      const amo = a.aracModel ? String(a.aracModel).trim() : "";
      const na = a.netAgirlik ? String(a.netAgirlik).trim() : "";
      const aya = a.azamiYukluAgirlik ? String(a.azamiYukluAgirlik).trim() : "";
      if (c && !res.cins.includes(c)) res.cins.push(c);
      if (t && !res.tesis.includes(t)) res.tesis.push(t);
      if (m && !res.model.includes(m)) res.model.push(m);
      if (b && !res.bom.includes(b)) res.bom.push(b);
      if (ugm && !res.ustGrupMarka.includes(ugm)) res.ustGrupMarka.push(ugm);
      if (ugmo && !res.ustGrupModel.includes(ugmo)) res.ustGrupModel.push(ugmo);
      if (am && !res.aracMarka.includes(am)) res.aracMarka.push(am);
      if (amo && !res.aracModel.includes(amo)) res.aracModel.push(amo);
      if (na && !res.netAgirlik.includes(na)) res.netAgirlik.push(na);
      if (aya && !res.azamiYukluAgirlik.includes(aya)) res.azamiYukluAgirlik.push(aya);
    });
    res.cins.sort(byLocale); res.tesis.sort(byLocale); res.model.sort(byLocale); res.bom.sort();
    res.ustGrupMarka.sort(byLocale); res.ustGrupModel.sort(byLocale); res.aracMarka.sort(byLocale); res.aracModel.sort(byLocale);
    res.netAgirlik.sort(); res.azamiYukluAgirlik.sort();
    return res;
  }, [araclar]);

  // --- GELİŞMİŞ FİLTRELEME MANTIĞI ---
  const filteredAraclar = useMemo(() => {
    return araclar.filter(a => {
      const plateMatch = (a.plate || "").toLocaleLowerCase("tr").includes(colFilters.plate.toLocaleLowerCase("tr"));
      
      const checkMatch = (val, filterKey) => {
        if (colFilters[filterKey] === "HEPSİ") return true;
        const stringVal = (val !== undefined && val !== null) ? String(val).trim() : "";
        if (colFilters[filterKey] === "BOŞ") return stringVal === "";
        return stringVal === colFilters[filterKey];
      };

      const cDate = a.createdTime?.toDate ? a.createdTime.toDate().toISOString().split('T')[0] : null;
      const uDate = a.updatedTime?.toDate ? a.updatedTime.toDate().toISOString().split('T')[0] : null;

      const createdMatch = (!colFilters.createdStart || (cDate && cDate >= colFilters.createdStart)) &&
                           (!colFilters.createdEnd || (cDate && cDate <= colFilters.createdEnd));
      
      const updatedMatch = (!colFilters.updatedStart || (uDate && uDate >= colFilters.updatedStart)) &&
                           (!colFilters.updatedEnd || (uDate && uDate <= colFilters.updatedEnd));

      return plateMatch && 
             checkMatch(a.aracCinsi, "aracCinsi") && 
             checkMatch(a.tesis, "tesis") && 
             checkMatch(a.model, "model") && 
             checkMatch(a.bomUzunlugu, "bomUzunlugu") &&
             checkMatch(a.ustGrupMarka, "ustGrupMarka") &&
             checkMatch(a.ustGrupModel, "ustGrupModel") &&
             checkMatch(a.aracMarka, "aracMarka") &&
             checkMatch(a.aracModel, "aracModel") &&
             checkMatch(a.netAgirlik, "netAgirlik") &&
             checkMatch(a.azamiYukluAgirlik, "azamiYukluAgirlik") &&
             (colFilters.active === "HEPSİ" || String(a.active) === colFilters.active) &&
             createdMatch && updatedMatch;
    });
  }, [araclar, colFilters]);

  const summary = useMemo(() => {
    const res = { total: filteredAraclar.length, byTesis: {}, details: {} };
    filteredAraclar.forEach(a => {
      const t = a.tesis || "BELİRTİLMEMİŞ";
      const c = (a.aracCinsi || "DİĞER").toUpperCase();
      res.byTesis[t] = (res.byTesis[t] || 0) + 1;
      if (!res.details[t]) res.details[t] = {};
      if (!res.details[t][c]) res.details[t][c] = [];
      res.details[t][c].push(a.plate);
    });
    return res;
  }, [filteredAraclar]);

  // Yardımcı input temizleme fonksiyonları
  const handleNumericInput = (val, length) => {
    const onlyNum = val.replace(/[^0-9]/g, '');
    return onlyNum.slice(0, length);
  };

  const columnDefs = useMemo(() => {
    const map = new Map(DEFAULT_COLUMNS.map(c => [c.key, c]));
    return columnLayout.map(c => ({ ...map.get(c.key), visible: c.visible !== false })).filter(c => c.key);
  }, [columnLayout]);

  const visibleColumns = useMemo(() => columnDefs.filter(c => c.visible), [columnDefs]);
  const hiddenColumns = useMemo(() => columnDefs.filter(c => !c.visible), [columnDefs]);

  const setColumnVisible = (key, visible) => {
    setColumnLayout(cols => cols.map(c => c.key === key ? { ...c, visible } : c));
  };

  const showColumnAt = (key, toKey) => {
    if (!key) return;
    setColumnLayout(cols => {
      const next = cols.map(c => c.key === key ? { ...c, visible: true } : c);
      if (!toKey) return next;
      const from = next.findIndex(c => c.key === key);
      const to = next.findIndex(c => c.key === toKey);
      if (from < 0 || to < 0 || from === to) return next;
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const saveColumnDesign = async () => {
    if (!user?.email) return alert("Kullanıcı bilgisi bulunamadı.");
    try {
      const designId = `${DESIGN_MODULE}_${encodeURIComponent(user.email)}`;
      await setDoc(doc(db, "reportDesigns", designId), {
        module: DESIGN_MODULE,
        name: "Araç Listesi",
        ownerEmail: user.email,
        columns: columnLayout,
        updatedAt: serverTimestamp()
      }, { merge: true });
      alert("Tablo tasarımı kaydedildi.");
    } catch (err) {
      alert("Tasarım kaydedilemedi: " + err.message);
    }
  };

  const renderColumnFilter = (key) => {
    const selectFilter = (filterKey, values) => (
      <select value={colFilters[filterKey]} onChange={e=>setColFilters({...colFilters, [filterKey]: e.target.value})} style={{ width: "100%", padding: "6px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}>
        <option value="HEPSİ">HEPSİ</option><option value="BOŞ">(BOŞ)</option>
        {values.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
    );

    if (key === "plate") {
      return <input placeholder="Ara..." value={colFilters.plate} onChange={e=>setColFilters({...colFilters, plate: e.target.value})} style={{ width: "100%", padding: "6px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}/>;
    }
    if (key === "aracCinsi") return selectFilter("aracCinsi", uniqueVals.cins);
    if (key === "tesis") return selectFilter("tesis", uniqueVals.tesis);
    if (key === "model") return selectFilter("model", uniqueVals.model);
    if (key === "bomUzunlugu") return selectFilter("bomUzunlugu", uniqueVals.bom);
    if (key === "ustGrupMarka") return selectFilter("ustGrupMarka", uniqueVals.ustGrupMarka);
    if (key === "ustGrupModel") return selectFilter("ustGrupModel", uniqueVals.ustGrupModel);
    if (key === "aracMarka") return selectFilter("aracMarka", uniqueVals.aracMarka);
    if (key === "aracModel") return selectFilter("aracModel", uniqueVals.aracModel);
    if (key === "netAgirlik") return selectFilter("netAgirlik", uniqueVals.netAgirlik);
    if (key === "azamiYukluAgirlik") return selectFilter("azamiYukluAgirlik", uniqueVals.azamiYukluAgirlik);
    if (key === "active") {
      return (
        <select value={colFilters.active} onChange={e=>setColFilters({...colFilters, active: e.target.value})} style={{ width: "100%", padding: "6px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}>
          <option value="HEPSİ">HEPSİ</option><option value="true">AKTİF</option><option value="false">PASİF</option>
        </select>
      );
    }
    if (key === "createdTime") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <input type="date" value={colFilters.createdStart} onChange={e=>setColFilters({...colFilters, createdStart: e.target.value})} style={{ fontSize: "10px", width: "100%", padding: "4px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}/>
          <input type="date" value={colFilters.createdEnd} onChange={e=>setColFilters({...colFilters, createdEnd: e.target.value})} style={{ fontSize: "10px", width: "100%", padding: "4px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}/>
        </div>
      );
    }
    if (key === "updatedTime") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <input type="date" value={colFilters.updatedStart} onChange={e=>setColFilters({...colFilters, updatedStart: e.target.value})} style={{ fontSize: "10px", width: "100%", padding: "4px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}/>
          <input type="date" value={colFilters.updatedEnd} onChange={e=>setColFilters({...colFilters, updatedEnd: e.target.value})} style={{ fontSize: "10px", width: "100%", padding: "4px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}/>
        </div>
      );
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cleanPlate     = formData.plate.replace(/\s+/g, '').toUpperCase();
    const cleanRuhsat    = (formData.ruhsatSeriNo || "").replace(/\s+/g, '').toUpperCase();
    const cleanMotor     = (formData.motorNo || "").replace(/\s+/g, '').toUpperCase();
    const cleanSase      = (formData.saseNo || "").replace(/\s+/g, '').toUpperCase();
    const cleanUstGrupMarka = toTitleCaseTR(formData.ustGrupMarka);
    const cleanUstGrupModel = toTitleCaseTR(formData.ustGrupModel);
    const cleanAracMarka    = toTitleCaseTR(formData.aracMarka);
    const cleanAracModel    = toTitleCaseTR(formData.aracModel);

    // Zorunlu alan kontrolü
    if (!cleanPlate)          return alert("Plaka zorunludur!");
    if (!formData.aracCinsi)  return alert("Araç Cinsi zorunludur!");
    if (!formData.tesis)      return alert("Tesis zorunludur!");
    if (!formData.model)      return alert("Model zorunludur!");
    if (formData.model.length !== 4) return alert("Model 4 haneli olmalıdır (Örn: 2024)");
    if (formData.bomUzunlugu && formData.bomUzunlugu.length > 2) return alert("Bom uzunluğu en fazla 2 hane olabilir.");

    // Mükerrer Plaka Kontrolü (Sadece Yeni Kayıtta)
    if (!editingId) {
      const isExist = araclar.some(a => a.plate === cleanPlate);
      if (isExist) return alert(`HATA: ${cleanPlate} plakası zaten kayıtlı!`);
    }

    try {
      const payload = {
        ...formData,
        plate:        cleanPlate,
        ruhsatSeriNo: cleanRuhsat,
        motorNo:      cleanMotor,
        saseNo:       cleanSase,
        ustGrupMarka: cleanUstGrupMarka,
        ustGrupModel: cleanUstGrupModel,
        aracMarka:    cleanAracMarka,
        aracModel:    cleanAracModel,
        updatedTime:  serverTimestamp(),
        updatedBy:    user?.email || "admin",
      };
      if (editingId) {
        await updateDoc(doc(db, "plates", editingId), payload);
      } else {
        await addDoc(collection(db, "plates"), { ...payload, createdTime: serverTimestamp(), createdBy: user?.email || "admin" });
      }
      resetForm(); fetchAraclar(izinliTesisler);
    } catch (err) { alert("Hata: " + err.message); }
  };

  const resetForm = () => {
    setFormData({
      plate: "", aracCinsi: "", tesis: "", model: "", bomUzunlugu: "",
      ruhsatSeriNo: "", motorNo: "", saseNo: "",
      ustGrupMarka: "", ustGrupModel: "", aracMarka: "", aracModel: "",
      netAgirlik: "", azamiYukluAgirlik: "",
      active: true
    });
    setEditingId(null);
    setMoreOpen(false);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 50 }}>Yükleniyor...</div>;

  return (
    <div style={{ padding: "20px", fontFamily: "sans-serif" }}>
      
      {/* 🟢 FORM ALANI */}
      <div style={{ backgroundColor: "#f8f9fa", border: "1px solid #ddd", padding: "20px", borderRadius: "8px", marginBottom: "25px" }}>
        <h3 style={{ marginTop: 0 }}>{editingId ? "🚨 Araç Güncelle" : "➕ Yeni Araç"}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", marginBottom: "15px" }}>
            <input 
              placeholder="Plaka (Boşluksuz) *" 
              value={formData.plate} 
              onChange={e=>setFormData({...formData, plate: e.target.value.replace(/\s+/g, '').toUpperCase()})} 
              style={{padding:8}}
            />
            <select 
              value={formData.aracCinsi} 
              onChange={e=>setFormData({...formData, aracCinsi: e.target.value})} 
              style={{padding:8}}
            >
              <option value="">Cins Seçiniz... *</option>
              {CINS_LISTESI.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select 
              value={formData.tesis} 
              onChange={e=>setFormData({...formData, tesis: e.target.value})} 
              style={{padding:8}}
            >
              <option value="">Tesis Seçiniz... *</option>
              {uniqueVals.tesis.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input 
              placeholder="Model Tarihi (4 Rakam) *" 
              value={formData.model} 
              onChange={e=>setFormData({...formData, model: handleNumericInput(e.target.value, 4)})} 
              style={{padding:8}}
            />
            <input 
              placeholder="Bom (2 Rakam)" 
              value={formData.bomUzunlugu} 
              onChange={e=>setFormData({...formData, bomUzunlugu: handleNumericInput(e.target.value, 2)})} 
              style={{padding:8}}
            />
            <input
              placeholder="Ruhsat No"
              value={formData.ruhsatSeriNo}
              onChange={e=>setFormData({...formData, ruhsatSeriNo: e.target.value.replace(/\s+/g,'').toUpperCase()})}
              style={{padding:8}}
            />
            <input
              placeholder="Motor No"
              value={formData.motorNo}
              onChange={e=>setFormData({...formData, motorNo: e.target.value.replace(/\s+/g,'').toUpperCase()})}
              style={{padding:8}}
            />
            <input
              placeholder="Şase No"
              value={formData.saseNo}
              onChange={e=>setFormData({...formData, saseNo: e.target.value.replace(/\s+/g,'').toUpperCase()})}
              style={{padding:8}}
            />
            <select value={formData.active} onChange={e=>setFormData({...formData, active: e.target.value==="true"})} style={{padding:8}}>
              <option value="true">Aktif</option><option value="false">Pasif</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => setMoreOpen(v => !v)}
            title={moreOpen ? "Daha az alan göster" : "Daha fazla alan göster"}
            style={{ marginBottom: 12, width: 28, height: 28, border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", cursor: "pointer", fontWeight: 800, fontSize: 18, lineHeight: "24px", padding: 0, color:"#000" }}
          >
            {moreOpen ? "−" : "+"}
          </button>
          {moreOpen && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", marginBottom: "15px", padding: 12, border: "1px dashed #cbd5e1", borderRadius: 8, background: "#fff" }}>
              <input
                placeholder="Üst Grup Marka"
                value={formData.ustGrupMarka || ""}
                onChange={e=>setFormData({...formData, ustGrupMarka: toTitleCaseTR(e.target.value)})}
                style={{padding:8}}
              />
              <input
                placeholder="Üst Grup Model"
                value={formData.ustGrupModel || ""}
                onChange={e=>setFormData({...formData, ustGrupModel: toTitleCaseTR(e.target.value)})}
                style={{padding:8}}
              />
              <input
                placeholder="Araç Marka"
                value={formData.aracMarka || ""}
                onChange={e=>setFormData({...formData, aracMarka: toTitleCaseTR(e.target.value)})}
                style={{padding:8}}
              />
              <input
                placeholder="Araç Modeli"
                value={formData.aracModel || ""}
                onChange={e=>setFormData({...formData, aracModel: toTitleCaseTR(e.target.value)})}
                style={{padding:8}}
              />
              <input
                placeholder="Net Ağırlığı"
                value={formData.netAgirlik || ""}
                onChange={e=>setFormData({...formData, netAgirlik: e.target.value})}
                style={{padding:8}}
              />
              <input
                placeholder="Azami Yüklü Ağırlığı"
                value={formData.azamiYukluAgirlik || ""}
                onChange={e=>setFormData({...formData, azamiYukluAgirlik: e.target.value})}
                style={{padding:8}}
              />
            </div>
          )}
          <div style={{ display: "flex", gap: "10px" }}>
            <button type="submit" style={{ padding: "10px 30px", backgroundColor: "#27ae60", color:"#fff", border:"none", borderRadius:4, cursor:"pointer", fontWeight:"bold" }}>KAYDET</button>
            {editingId && <button onClick={resetForm} style={{ padding:"10px 20px", background:"#7f8c8d", color:"#fff", border:"none", borderRadius:4 }}>İptal</button>}
          </div>
        </form>
      </div>

      {/* 📊 TABLO VE FİLTRELER */}
      <div style={{ marginBottom: 12, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
          <strong>Tablo Alanları</strong>
          <button onClick={saveColumnDesign} style={{ padding: "7px 12px", background: "#2563eb", color: "#fff", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: 700 }}>
            Tasarımı Kaydet
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {visibleColumns.map(col => (
            <span
              key={col.key}
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/plain", col.key)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => showColumnAt(e.dataTransfer.getData("text/plain"), col.key)}
              title="Sürükleyerek sıralayabilirsiniz"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 9px", border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", cursor: "grab", fontSize: 12 }}
            >
              <span style={{ color: "#64748b" }}>↕</span>
              <span>{col.label}</span>
              <button
                type="button"
                onClick={() => setColumnVisible(col.key, false)}
                title="Listeden gizle"
                style={{ border: 0, background: "transparent", cursor: "pointer", color: "#64748b", padding: 0 }}
              >
                x
              </button>
            </span>
          ))}
        </div>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => setColumnVisible(e.dataTransfer.getData("text/plain"), false)}
          style={{ marginTop: 10, padding: 10, border: "1px dashed #cbd5e1", borderRadius: 8, background: "#fff" }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: "#475569" }}>Gizli sütunlar</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", minHeight: 28 }}>
            {hiddenColumns.map(col => (
              <span
                key={col.key}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("text/plain", col.key)}
                onClick={() => setColumnVisible(col.key, true)}
                title="Tabloya geri almak için tıklayın veya başlıkların üzerine sürükleyin"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 9px", border: "1px solid #cbd5e1", borderRadius: 8, background: "#f1f5f9", cursor: "grab", fontSize: 12 }}
              >
                <span>{col.label}</span>
              </span>
            ))}
            {hiddenColumns.length === 0 && <span style={{ fontSize: 12, color: "#94a3b8" }}>Gizli sütun yok</span>}
          </div>
        </div>
      </div>
      <div style={{ overflowX: "auto", backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #e0e0e0", boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ backgroundColor: "#34495e", color: "#444" }}>
              {visibleColumns.map(col => (
                <th
                  key={col.key}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", col.key)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => showColumnAt(e.dataTransfer.getData("text/plain"), col.key)}
                  title="Başlığı sürükleyerek sütun yerini değiştirebilirsiniz"
                  style={{ padding: "12px 8px", minWidth: col.width || "120px", textAlign: col.align || "left", borderRight: "1px solid #455a64", color: "#111", cursor: "grab" }}
                >
                  {col.label}
                </th>
              ))}
              <th style={{ padding: "12px 8px", textAlign: "center" }}>İŞLEM</th>
            </tr>
            <tr style={{ backgroundColor: "#f8f9fa" }}>
              {visibleColumns.map(col => (
                <td key={col.key} style={{ padding: "5px" }}>{renderColumnFilter(col.key)}</td>
              ))}
              <td></td>
            </tr>
          </thead>
          <tbody>
            {filteredAraclar.map((arac, index) => (
              <tr key={arac.id} style={{ borderBottom: "1px solid #eee", backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa" }}>
                {visibleColumns.map(col => (
                  <td key={col.key} style={{ padding: "10px 8px", fontWeight: col.bold ? "bold" : "normal", color: col.bold ? "#2c3e50" : "#333", textAlign: col.align || "left" }}>
                    {col.key === "active" ? (
                      <span style={{ padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "bold", backgroundColor: arac.active ? "#e8f5e9" : "#ffebee", color: arac.active ? "#2e7d32" : "#c62828" }}>
                        {col.render(arac)}
                      </span>
                    ) : col.render(arac)}
                  </td>
                ))}
                <td style={{ padding: "10px 8px", textAlign: "center" }}>
                  <button onClick={() => { setEditingId(arac.id); setFormData({...arac}); setMoreOpen(Boolean(arac.ustGrupMarka || arac.ustGrupModel || arac.aracMarka || arac.aracModel || arac.netAgirlik || arac.azamiYukluAgirlik)); window.scrollTo(0,0); }} style={{ background: "#3498db", color: "#fff", border: "none", padding: "5px 12px", cursor: "pointer", borderRadius: "4px", fontSize: "12px" }}>Düzenle</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 📋 ÖZET */}
      <div style={{ marginTop: "30px", padding: "20px", backgroundColor: "#fdfdfd", border: "1px solid #eee", borderRadius: "8px" }}>
        <h3>📊 Genel Özet ({summary.total})</h3>
        {Object.entries(summary.byTesis).sort(([a],[b])=>byLocale(a,b)).map(([t, n]) => (
          <div key={t} style={{ marginBottom: 10 }}>
            <button onClick={() => setTesisOpen(s => ({ ...s, [t]: !s[t] }))} style={{ cursor: "pointer", width: 25 }}>{tesisOpen[t] ? "−" : "+"}</button>
            <strong style={{marginLeft:10}}>{t}: {n} Araç</strong>
            {tesisOpen[t] && (
              <div style={{ paddingLeft: 40, marginTop: 5, fontSize: 12, color: "#555" }}>
                {Object.entries(summary.details[t]).map(([cins, plates]) => (
                  <div key={cins}>{cins}: {plates.join(", ")}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AracListesi;
